import { useIonRouter } from '@ionic/react';
import { useEffect, useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import { AppIonPage } from '@/core/components/app-ion-page';
import {
  PUBLIC_PROFILE_PATH_PATTERN,
  shellPaths,
} from '@/core/router/app-paths';
import { isAppSessionActive } from '@/core/router/auth-route-gate';
import { useAuthStore } from '@/core/store/auth.store';
import { openLinkResolveFromProfile } from '@/core/store/link-resolve.store';

const PUBLIC_PROFILE_PATH = PUBLIC_PROFILE_PATH_PATTERN;

function resolvePublicProfileParams(
  params: { workspaceUsername?: string; username?: string },
  pathname: string,
): { workspaceUsername?: string; username?: string } {
  if (params.workspaceUsername && params.username) {
    return {
      workspaceUsername: params.workspaceUsername,
      username: params.username,
    };
  }

  const matched = matchPath({ path: PUBLIC_PROFILE_PATH, end: true }, pathname);

  return {
    workspaceUsername: matched?.params?.workspaceUsername,
    username: matched?.params?.username,
  };
}

/**
 * Compat shim for legacy / notification / guest public-profile routes.
 * Opens the link-resolve dialog and leaves authenticated users on Leads.
 */
export function PublicProfilePage() {
  const routeParams = useParams<{
    workspaceUsername: string;
    username: string;
  }>();
  const location = useLocation();
  const { workspaceUsername, username } = resolvePublicProfileParams(
    routeParams,
    location.pathname,
  );
  const router = useIonRouter();
  const authStatus = useAuthStore((state) => state.authStatus);

  const scanHash = useMemo(() => {
    const value = new URLSearchParams(location.search).get('scanHash')?.trim();
    return value || null;
  }, [location.search]);

  useEffect(() => {
    if (!workspaceUsername || !username) return;

    openLinkResolveFromProfile(workspaceUsername, username, scanHash);

    if (!isAppSessionActive(authStatus)) {
      return;
    }

    router.push(shellPaths.leads, 'root', 'replace');
  }, [workspaceUsername, username, scanHash, authStatus, router]);

  return <AppIonPage />;
}
